import {
  Avatar,
  Button,
  Card,
  Col,
  List,
  Row,
  Space,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useFetchUserDataQuery } from '@/store/apis/userApi';
import Meeting from '@/components/Meeting/Meeting';
import { storageUtil } from '@/utils/storageUtil';
import odinIcon from '@/assets/images/odin-icon-white.webp';

interface Specialist {
  key: string;
  name: string;
  role: string;
  description: string;
  meetingUrl: string;
}

const TAX_SPECIALISTS: Specialist[] = [
  {
    key: 'PAU_LAM',
    name: 'Pau Lam',
    role: 'Director of Tax Services',
    description: 'Tax consultation',
    meetingUrl: 'https://meetings.hubspot.com/pau-lam/tax-meeting',
  },
  {
    key: 'ALAN_QUAN',
    name: 'Alan Quan',
    role: 'Senior Expat Tax Specialist',
    description: 'Tax consultation',
    meetingUrl: 'https://meetings.hubspot.com/alan-quan/tax-meeting',
  },
  {
    key: 'AARON_B',
    name: 'Aaron B',
    role: 'Senior Expat Tax Specialist',
    description: 'Tax consultation',
    meetingUrl:
      'https://meetings.hubspot.com/aaronb/australian-taxation-consultation',
  },
];

const STORAGE_KEY = 'tax_specialist_selection';

const getInitials = (name: string): string =>
  name
    .split(' ')
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);

const buildMeetingUrl = (
  baseUrl: string,
  userProfile?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  },
): string => {
  const params = new URLSearchParams({ embed: 'true' });

  if (userProfile?.firstName) params.set('firstname', userProfile.firstName);
  if (userProfile?.lastName) params.set('lastname', userProfile.lastName);
  if (userProfile?.email) params.set('email', userProfile.email);
  if (userProfile?.phone) params.set('phone', userProfile.phone);

  return `${baseUrl}?${params}`;
};

export const TaxAdvicePage = () => {
  const { data: userProfileData } = useFetchUserDataQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const userProfile = userProfileData?.data;

  const [selectedKey, setSelectedKey] = useState<string | null>(() =>
    storageUtil.getItem(STORAGE_KEY),
  );
  const [meetingUrl, setMeetingUrl] = useState<string>('');

  const selectedSpecialist = TAX_SPECIALISTS.find((s) => s.key === selectedKey);

  useEffect(() => {
    if (!selectedSpecialist) {
      setMeetingUrl('');
      return;
    }

    setMeetingUrl(buildMeetingUrl(selectedSpecialist.meetingUrl, userProfile));
  }, [selectedSpecialist, userProfile]);

  const handleSelect = (key: string) => {
    storageUtil.setItem(STORAGE_KEY, key);
    setSelectedKey(key);
  };

  const handleBack = () => {
    storageUtil.removeItem(STORAGE_KEY);
    setSelectedKey(null);
  };

  return (
    <div
      style={{
        minHeight: 'calc(100vh - 80px)',
        padding: '40px 16px 64px',
        background: 'linear-gradient(180deg, #f5f9fd 0%, #ffffff 60%)',
      }}
    >
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        {!selectedSpecialist ? (
          <>
            <Card
              styles={{ body: { padding: '36px 28px', textAlign: 'center' } }}
              style={{ background: '#003862', borderRadius: 16 }}
            >
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Avatar
                  shape="square"
                  size={52}
                  src={odinIcon}
                  style={{ background: 'rgba(255,255,255,0.14)', padding: 10 }}
                />
                <Typography.Title
                  level={2}
                  style={{ color: '#fff', margin: 0 }}
                >
                  Book your expat tax consultation
                </Typography.Title>
                <Typography.Paragraph
                  style={{
                    color: 'rgba(255,255,255,0.8)',
                    fontSize: 16,
                    maxWidth: 620,
                    margin: '0 auto',
                  }}
                >
                  Choose a specialist and schedule a meeting with pre-filled
                  details from your profile.
                </Typography.Paragraph>
              </Space>
            </Card>

            <Typography.Title
              level={5}
              type="secondary"
              style={{ textAlign: 'center', margin: '32px 0 18px' }}
            >
              Select your preferred specialist
            </Typography.Title>

            <Row gutter={[20, 20]} justify="center">
              {TAX_SPECIALISTS.map((specialist) => (
                <Col key={specialist.key} xs={24} sm={12} md={8}>
                  <Card
                    hoverable
                    onClick={() => handleSelect(specialist.key)}
                    style={{ height: '100%', borderRadius: 12 }}
                    styles={{ body: { textAlign: 'center' } }}
                  >
                    <Space
                      direction="vertical"
                      size={12}
                      style={{ width: '100%' }}
                    >
                      <Avatar
                        size={72}
                        style={{
                          background: '#e6f1fb',
                          color: '#185fa5',
                          fontSize: 22,
                          fontWeight: 700,
                        }}
                      >
                        {getInitials(specialist.name)}
                      </Avatar>

                      <Typography.Title level={4} style={{ margin: 0 }}>
                        {specialist.name}
                      </Typography.Title>

                      <Tag
                        color="blue"
                        style={{ borderRadius: 999, margin: 0 }}
                      >
                        {specialist.role}
                      </Tag>

                      <Typography.Text type="secondary">
                        {specialist.description}
                      </Typography.Text>

                      <Button
                        type="primary"
                        block
                        icon={<CalendarOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelect(specialist.key);
                        }}
                      >
                        Book with {specialist.name.split(' ')[0]}
                        <RightOutlined />
                      </Button>
                    </Space>
                  </Card>
                </Col>
              ))}
            </Row>
          </>
        ) : (
          <>
            <Card
              style={{ marginBottom: 18, borderRadius: 12 }}
              styles={{ body: { padding: '12px 18px' } }}
            >
              <Row align="middle" justify="space-between" gutter={[16, 8]}>
                <Col>
                  <Button
                    type="text"
                    icon={<ArrowLeftOutlined />}
                    onClick={handleBack}
                  >
                    Back to specialists
                  </Button>
                </Col>
                <Col>
                  <List.Item.Meta title={selectedSpecialist.name} />
                </Col>
              </Row>
            </Card>

            <Meeting meetingUrl={meetingUrl} title="Book a Meeting" />
          </>
        )}
      </div>
    </div>
  );
};

export default TaxAdvicePage;
